import { useEffect, useState } from 'react'
import { useSession } from '../misc/session_context'
import useStateWithLocalStorage from '../hooks/use_state_with_local_storage'

import { storefrontFetch } from '../misc/fetch_helpers'
import useCsrfToken from '../helpers/use_csrf_token'
import SalePlan from './sale_plan'

export const emptyCart = {
  plan: {
    cart: [],
    shipType: null,
    preferredShippingMethod: null
  }
}

export default function useSaleCart() {
  const [sessionData, setSessionData] = useSession()

  const useCsrfTokenPromise = useCsrfToken()

  const [state, setState] = useState({
    loading: true,
    updating: true,
    error: null
  })

  const [cart, setCart] = useStateWithLocalStorage('saleCart', { ...emptyCart })

  // need to use a closure here because `setSaleCart({ ..., plan: newSalePlan })` is asynchronous and not reliable for what we need to check here
  let notStatePlan
  const finish = (finalPlan, newSalePlan) => {
    if (notStatePlan === newSalePlan) {
      const itemCount = finalPlan.cart.reduce((accumulator, item) => accumulator + item.quantity, 0)
      setState({
        updating: false
      })
      setCart({ plan: finalPlan })
      setSessionData({ ...sessionData, saleCart: { size: itemCount } })
    } else {
      setState({ ...state })
      setSessionData({ ...sessionData })
    }
  }

  const updateSaleCart = async newSalePlan => {
    if (!(newSalePlan instanceof SalePlan)) throw new Error('must pass a SalePlan')

    const originalPlan = cart.plan
    notStatePlan = newSalePlan
    setState({
      error: null,
      updating: true
    })
    setCart({ plan: newSalePlan })

    // make sure to get the freshest token
    const csrfToken = await useCsrfTokenPromise

    storefrontFetch('/sale_cart.json', {
      method: 'put',
      // manually pass this to make sure the most up to date token is used
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
        'X-Csrf-Token': csrfToken
      },
      // we don't need to post the entire `newSalePlan` or the requst can become GIANT
      body: {
        cart: newSalePlan.cart.map(item => ({
          saleListing: {
            id: item.saleListing.id
          },
          quantity: item.quantity,
          hasWarranty: item.hasWarranty,
          newlyAdded: item.newlyAdded
        })),
        shipType: newSalePlan.shipType,
        preferredShippingMethod: newSalePlan.preferredShippingMethod
      }
    })
      .then(newServerSidePlan => {
        return finish(newServerSidePlan, newSalePlan)
      })
      .catch(error => {
        if (process.env.NODE_ENV === 'development') {
          console.log(error)
        }
        return finish(originalPlan, newSalePlan)
      })
  }

  useEffect(() => {
    const getSaleCart = async () => {
      // make sure to get the freshest token
      const csrfToken = await useCsrfTokenPromise

      return storefrontFetch('/sale_cart.json', {
        method: 'put',
        // manually pass this to make sure the most up to date token is used
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
          'X-Csrf-Token': csrfToken
        },
        body: {
          cart: cart.plan.cart.map(item => ({
            saleListing: {
              id: item.saleListing.id
            },
            quantity: item.quantity,
            hasWarranty: item.hasWarranty
          })),
          shipType: cart.plan.shipType,
          preferredShippingMethod: cart.plan.preferredShippingMethod
        }
      })
        .then(data => {
          setState({
            loading: false,
            updating: false,
            error: null
          })
          setCart({ plan: data })
        })
        .catch(error => {
          if (process.env.NODE_ENV === 'development') {
            console.log('error getting sale plan', error)
          }
          setState({
            loading: false,
            updating: false,
            error: 'Uh oh, unable to get your cart.'
          })
          // puts the back to the last known plan, which may be the `emptyCart`
          setCart(cart)
        })
    }

    getSaleCart()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return [
    {
      ...state,
      // makes sure this always return an instance of a SalePlan
      plan: new SalePlan(cart.plan)
    },
    updateSaleCart
  ]
}
